import React, { createContext, useContext, useState, useCallback } from 'react';
import toast from 'react-hot-toast';

const ErrorContext = createContext();

export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};

export const ErrorProvider = ({ children }) => {
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Clear all errors
  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  // Clear specific field error
  const clearFieldError = useCallback((field) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  // Set field-specific errors (for form validation)
  const setFieldErrors = useCallback((fieldErrors) => {
    setErrors(fieldErrors);
  }, []);

  // Handle API errors with proper Laravel error parsing
  const handleApiError = useCallback((error, options = {}) => {
    const {
      showToast = true,
      toastDuration = 5000,
      fallbackMessage = 'An error occurred'
    } = options;

    console.error('API Error:', error);

    let errorMessage = fallbackMessage;
    let fieldErrors = {};

    if (error.response) {
      const { status, data } = error.response;

      // Handle different HTTP status codes
      switch (status) {
        case 400:
          errorMessage = data.message || 'Bad Request';
          break;
        case 401:
          errorMessage = 'Unauthorized access';
          // Don't show toast for auth errors as they're handled by interceptor
          if (showToast) {
            toast.error(errorMessage, { duration: toastDuration });
          }
          return { errorMessage, fieldErrors };
        case 403:
          errorMessage = data.message || 'Access forbidden';
          break;
        case 404:
          errorMessage = data.message || 'Resource not found';
          break;
        case 422:
          // Laravel validation errors
          if (data.errors) {
            fieldErrors = data.errors;
            errorMessage = data.message || 'Validation failed';
            
            // Show individual field errors
            Object.entries(fieldErrors).forEach(([field, messages]) => {
              if (Array.isArray(messages)) {
                messages.forEach(message => {
                  toast.error(`${field}: ${message}`, { duration: toastDuration });
                });
              }
            });
          } else {
            errorMessage = data.message || 'Validation failed';
          }
          break;
        case 429:
          errorMessage = 'Too many requests. Please try again later.';
          break;
        case 500:
          errorMessage = 'Internal server error. Please try again later.';
          break;
        case 503:
          errorMessage = 'Service unavailable. Please try again later.';
          break;
        default:
          errorMessage = data.message || `Error ${status}`;
      }
    } else if (error.request) {
      errorMessage = 'Network error. Please check your connection.';
    } else {
      errorMessage = error.message || fallbackMessage;
    }

    // Set field errors for form components
    if (Object.keys(fieldErrors).length > 0) {
      setFieldErrors(fieldErrors);
    }

    // Show toast notification
    if (showToast && error.response?.status !== 422) {
      toast.error(errorMessage, { duration: toastDuration });
    }

    return { errorMessage, fieldErrors };
  }, [setFieldErrors]);

  // Success handler
  const handleSuccess = useCallback((message, options = {}) => {
    const { showToast = true, toastDuration = 3000 } = options;
    
    if (showToast) {
      toast.success(message, { duration: toastDuration });
    }
  }, []);

  // Info handler
  const handleInfo = useCallback((message, options = {}) => {
    const { showToast = true, toastDuration = 3000 } = options;
    
    if (showToast) {
      toast(message, { 
        duration: toastDuration,
        icon: 'ℹ️',
        style: {
          background: '#3B82F6',
          color: '#fff',
        },
      });
    }
  }, []);

  // Warning handler
  const handleWarning = useCallback((message, options = {}) => {
    const { showToast = true, toastDuration = 4000 } = options;
    
    if (showToast) {
      toast(message, { 
        duration: toastDuration,
        icon: '⚠️',
        style: {
          background: '#F59E0B',
          color: '#fff',
        },
      });
    }
  }, []);

  // Loading state management
  const setLoading = useCallback((loading) => {
    setIsLoading(loading);
  }, []);

  const value = {
    errors,
    isLoading,
    clearErrors,
    clearFieldError,
    setFieldErrors,
    handleApiError,
    handleSuccess,
    handleInfo,
    handleWarning,
    setLoading,
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
    </ErrorContext.Provider>
  );
};
